import { Component, Inject, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { VrboHost2GuestReviewCategories } from '@tokeet-frontend/channels'
import { isSomething, SaveForm, Toaster } from '@tokeet-frontend/tv3-platform'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import * as lodash from 'lodash'
import { filter, finalize, map } from 'rxjs/operators'
import {
  ChannelReviewCategoryRating,
  getChannelReviewSource,
  isReviewGuestExpired,
  ReviewService,
} from '@tokeet-frontend/reviews'

@Component({
  selector: 'app-review-vrbo-guest-dialog',
  templateUrl: './review-vrbo-guest-dialog.component.html',
  styleUrls: ['./review-vrbo-guest-dialog.component.scss'],
})
export class ReviewVrboGuestDialogComponent implements OnInit {
  form = this.fb.group({
    starRatings: this.fb.array([]),
    recommendGuest: [null, [Validators.required]],
  })

  get categoryRatings() {
    return this.form.get('starRatings') as FormArray
  }

  isEditable = true
  isExpired = false
  isGeneratingAIReview = false

  categories = Object.values(VrboHost2GuestReviewCategories)

  isLoading = true

  get inquiry() {
    return this.data.inquiry
  }

  constructor(
    public dialogRef: MatDialogRef<ReviewVrboGuestDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private reviewService: ReviewService,
    @Inject(MAT_DIALOG_DATA) public data: { inquiry: Inquiry }
  ) {}

  ngOnInit(): void {
    this.reviewService
      .getBookingReviews(this.inquiry.id, [getChannelReviewSource(this.inquiry.inquirySource)])
      .pipe(
        finalize(() => (this.isLoading = false)),
        map((data) => data.reviewToGuest),
        filter(isSomething)
      )
      .subscribe(
        (review) => {
          this.form.patchValue({
            starRatings: review.categoryRatings,
            recommendGuest: review.isRevieweeRecommended,
          })

          this.isExpired = isReviewGuestExpired(review)
          this.isEditable = !review.submitted && !this.isExpired
          if (review.submitted) {
            lodash.forEach(review.categoryRatings, (item) => {
              this.addCategoryRating(item)
            })
          } else if (this.isEditable) {
            const bookingCatRatings = this.inquiry.ratings
            lodash.forEach(this.categories, (c) =>
              this.addCategoryRating({
                category: c,
                rating: bookingCatRatings?.find((t) => t.type.includes(c))?.stars || null,
              })
            )
          }
          if (!this.isEditable) {
            this.form.disable()
          }
        },
        (error) => {
          this.toaster.error($localize`You cannot review this guest now`, '', error)
          this.form.disable()
        }
      )
  }

  onRecommend(v: boolean) {
    this.form.patchValue({ recommendGuest: v })
  }

  onRate(form: FormGroup, v: number) {
    form.patchValue({ value: v })
  }

  addCategoryRating(data?: Partial<ChannelReviewCategoryRating>) {
    const item = this.fb.group({
      category: [data?.category, [Validators.required]],
      value: [data?.rating, [Validators.required]],
    })
    item.get('category').disable()
    this.categoryRatings.push(item)
  }

  removeCategoryRating(index: number) {
    this.categoryRatings.removeAt(index)
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const payload = this.form.getRawValue()
    this.isLoading = true
    this.reviewService
      .reviewVrboGuest(this.inquiry.inquiryId, payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`Review saved successfully`)
          this.dialogRef.close()
        },
        (error) => {
          this.toaster.error($localize`Unable to save review`, '', error)
        }
      )
  }
}
